import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { workspaceAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
  Users, UserPlus, Shield, Trash2, Loader2, Crown, PenSquare, Eye, Info
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const roleConfig = {
  owner: {
    label: 'Owner',
    color: 'bg-gold/15 text-gold border border-gold/30',
    icon: Crown,
    desc: 'Full access — can delete workspace, manage billing, and all features.',
  },
  admin: {
    label: 'Admin',
    color: 'bg-primary/15 text-primary border border-primary/20',
    icon: Shield,
    desc: 'Full access to all features except deleting the workspace.',
  },
  manager: {
    label: 'Manager',
    color: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20',
    icon: Shield,
    desc: 'Manage content, approve posts, view analytics, manage team.',
  },
  team: {
    label: 'Team',
    color: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
    icon: PenSquare,
    desc: 'View and update assigned tasks, time tracking, and team collaboration.',
  },
  client: {
    label: 'Client',
    color: 'bg-secondary text-muted-foreground border border-border',
    icon: Eye,
    desc: 'Read-only access to calendar, captions, analytics. Can approve/reject posts.',
  },
};

export default function TeamPage() {
  const { currentWorkspace } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('team');
  const [inviting, setInviting] = useState(false);
  const [showRoleInfo, setShowRoleInfo] = useState(false);

  useEffect(() => {
    if (currentWorkspace?.id) {
      setLoading(true);
      workspaceAPI.listMembers(currentWorkspace.id)
        .then(res => setMembers(res.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [currentWorkspace?.id]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Email required'); return; }
    if (!inviteFirstName.trim()) { toast.error('First name required'); return; }
    setInviting(true);
    try {
      await workspaceAPI.inviteMember(currentWorkspace.id, {
        email: inviteEmail,
        role: inviteRole,
        first_name: inviteFirstName.trim(),
        last_name: inviteLastName.trim(),
      });
      toast.success(`Invited ${inviteFirstName} (${inviteEmail}) as ${roleConfig[inviteRole]?.label}`);
      setShowInvite(false);
      setInviteEmail(''); setInviteFirstName(''); setInviteLastName('');
      setInviteRole('team');
      const res = await workspaceAPI.listMembers(currentWorkspace.id);
      setMembers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId) => {
    try {
      await workspaceAPI.removeMember(currentWorkspace.id, memberId);
      toast.success('Member removed');
      setMembers(prev => prev.filter(m => m.id !== memberId));
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const clientCount = members.filter(m => m.role === 'client').length;
  const staffCount = members.filter(m => m.role !== 'client').length;

  return (
    <div className="p-6 space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Team & Access</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {staffCount} team member{staffCount !== 1 ? 's' : ''}, {clientCount} client{clientCount !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowRoleInfo(true)}>
            <Info className="h-4 w-4 mr-2" /> Access Levels
          </Button>
          <Button onClick={() => setShowInvite(true)} data-testid="team-invite-button">
            <UserPlus className="h-4 w-4 mr-2" /> Invite Member
          </Button>
        </div>
      </div>

      {/* How-to Card */}
      <Card className="bg-card/60 backdrop-blur border-gold/20 border-l-4" style={{ borderLeftColor: 'hsl(40, 72%, 48%)' }}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-gold shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm text-gold">How to share with your clients</p>
              <p className="text-xs text-muted-foreground mt-1">
                <strong>1.</strong> Have your client create an account on the login page. <br />
                <strong>2.</strong> Click "Invite Member" and enter their email with the <strong>"Client"</strong> role. <br />
                <strong>3.</strong> Your client will see a dedicated <strong>Client Portal</strong> with: content calendar, post captions & timings, analytics, and approval inbox to review content. <br />
                <strong>4.</strong> Clients can approve or reject posts directly from their portal.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agency Team Section */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Agency Team</h2>
        {loading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => <div key={i} className="h-16 bg-secondary rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {members.filter(m => m.role !== 'client').map(member => {
              const user = member.user || {};
              const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
              const config = roleConfig[member.role] || roleConfig.editor;
              const RoleIcon = config.icon;
              return (
                <Card key={member.id} className="bg-card/60 backdrop-blur">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-gold/20 text-gold text-sm">{initials}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{user.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={`text-xs ${config.color}`}>
                          <RoleIcon className="h-3 w-3 mr-1" />
                          {config.label}
                        </Badge>
                        {member.role !== 'owner' && member.role !== 'admin' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleRemove(member.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Clients Section */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Clients</h2>
        {members.filter(m => m.role === 'client').length === 0 ? (
          <Card className="bg-card/60 backdrop-blur">
            <CardContent className="p-6 text-center">
              <Eye className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium">No clients added yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Invite a client to give them read-only access to the content calendar, captions, timings, and analytics.
              </p>
              <Button size="sm" className="mt-3" onClick={() => { setInviteRole('client'); setShowInvite(true); }}>
                <UserPlus className="h-4 w-4 mr-2" /> Invite Client
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {members.filter(m => m.role === 'client').map(member => {
              const user = member.user || {};
              const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
              const config = roleConfig.client;
              return (
                <Card key={member.id} className="bg-card/60 backdrop-blur">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-secondary text-muted-foreground text-sm">{initials}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{user.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={`text-xs ${config.color}`}>
                          <Eye className="h-3 w-3 mr-1" />
                          Client
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleRemove(member.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-card border-border/40">
          <DialogHeader>
            <DialogTitle className="font-display text-[17px]">Invite to {currentWorkspace?.name}</DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground/50">They'll receive an email with login credentials and access to the workspace.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[12px]">First Name *</Label>
                <Input placeholder="First name" value={inviteFirstName} onChange={e => setInviteFirstName(e.target.value)} className="h-10 text-[13px] bg-foreground/[0.02] border-border/30" data-testid="invite-first-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px]">Last Name</Label>
                <Input placeholder="Last name" value={inviteLastName} onChange={e => setInviteLastName(e.target.value)} className="h-10 text-[13px] bg-foreground/[0.02] border-border/30" data-testid="invite-last-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px]">Email Address *</Label>
              <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="name@company.com" className="h-10 text-[13px] bg-foreground/[0.02] border-border/30" data-testid="invite-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px]">Access Level</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="h-10 text-[13px] bg-foreground/[0.02] border-border/30" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(roleConfig).filter(([key]) => key !== 'owner').map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2"><config.icon className="h-3 w-3" /><span>{config.label}</span></div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground/50">{roleConfig[inviteRole]?.desc}</p>
            </div>
            <div className="rounded-lg bg-foreground/[0.02] border border-border/20 p-3">
              <p className="text-[11px] text-muted-foreground/50">They will receive an email with a temporary password (<strong>Stellar121</strong>). On first login, they'll create their own password.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)} className="text-[13px]">Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} className="text-[13px] bg-gold/10 hover:bg-gold/20 text-gold border-0" data-testid="send-invite-button">
              {inviting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Levels Info Dialog */}
      <Dialog open={showRoleInfo} onOpenChange={setShowRoleInfo}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Access Levels</DialogTitle>
            <DialogDescription>
              Each role determines what a person can see and do in this workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {Object.entries(roleConfig).map(([key, config]) => (
              <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30">
                <Badge className={`text-xs mt-0.5 ${config.color}`}>
                  <config.icon className="h-3 w-3 mr-1" />
                  {config.label}
                </Badge>
                <p className="text-xs text-muted-foreground flex-1">{config.desc}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
